import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown } from '@phosphor-icons/react';
import { SILHOUETTE_AVATAR, getAvatarUrl } from '../lib/avatar';
import api from '../lib/api';

// Shuffle interval (ms) — rotates the list every N seconds to keep the bar feeling alive.
const SHUFFLE_INTERVAL_MS = 3 * 60 * 1000;   // every 3 minutes
const REFETCH_INTERVAL_MS = 10 * 60 * 1000;  // re-fetch fresh data every 10 minutes

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function StoriesBar({ filters }) {
  const [stories, setStories] = useState([]);
  const [shuffleKey, setShuffleKey] = useState(0);
  const navigate = useNavigate();
  const mountedRef = useRef(true);

  // Serialize filters into a stable string for the effect dep (avoid referential churn)
  const filterKey = filters ? JSON.stringify(filters) : '';

  useEffect(() => {
    mountedRef.current = true;
    const fetchStories = async () => {
      try {
        const params = {};
        if (filters) {
          if (filters.country && filters.country !== 'all') params.country = filters.country;
          if (filters.currentCountry && filters.currentCountry !== 'all') params.current_country = filters.currentCountry;
          if (filters.city) params.city = filters.city;
          if (filters.gender && filters.gender !== 'both') params.gender = filters.gender;
          if (Array.isArray(filters.ageRange)) {
            if (filters.ageRange[0] !== 18) params.min_age = filters.ageRange[0];
            if (filters.ageRange[1] !== 60) params.max_age = filters.ageRange[1];
          }
          if (filters.verifiedOnly) params.verified_only = true;
        }
        const { data } = await api.get('/stories', { params });
        if (!mountedRef.current) return;
        setStories(shuffle(data.stories || []));
      } catch {}
    };
    fetchStories();

    // Re-fetch fresh data periodically so new active users appear
    const refetch = setInterval(fetchStories, REFETCH_INTERVAL_MS);
    // Shuffle existing list every few minutes for dynamism
    const shuf = setInterval(() => setShuffleKey((k) => k + 1), SHUFFLE_INTERVAL_MS);

    return () => {
      mountedRef.current = false;
      clearInterval(refetch);
      clearInterval(shuf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  // Re-shuffle when shuffleKey changes — deterministic per render via useMemo so hover state doesn't re-trigger
  const displayed = useMemo(() => shuffle(stories), [stories, shuffleKey]);

  if (displayed.length === 0) return null;

  const getPhoto = (user) => getAvatarUrl(user) || SILHOUETTE_AVATAR;

  return (
    <div className="mb-6" data-testid="stories-bar">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-3">Active Now</p>
      <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {displayed.map((user) => (
          <button
            key={user.id}
            onClick={() => navigate(`/profile/${user.id}`)}
            className="flex flex-col items-center gap-1.5 flex-shrink-0 group transition-[order] duration-500"
            data-testid={`story-${user.id}`}
          >
            {/* Ring wrapper */}
            <div className={`relative p-[3px] rounded-full transition-all duration-300 group-hover:scale-110 ${
              user.is_premium || user.is_verified
                ? 'bg-gradient-to-br from-[#D4AF37] via-[#F0C84A] to-[#D4AF37] shadow-[0_0_12px_rgba(212,175,55,0.4)]'
                : 'bg-gradient-to-br from-[#9EABCE]/40 to-[#9EABCE]/20'
            }`}>
              <div className="p-[2px] rounded-full bg-[#060A1A]">
                <div className="w-16 h-16 md:w-[72px] md:h-[72px] rounded-full overflow-hidden">
                  <img
                    src={getPhoto(user)}
                    alt={user.first_name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              {/* Premium crown badge */}
              {(user.is_premium || user.is_verified) && (
                <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-[#D4AF37] flex items-center justify-center shadow-lg animate-heartbeat">
                  <Crown size={11} weight="fill" className="text-[#060A1A]" />
                </div>
              )}
            </div>
            <span className={`text-xs font-medium truncate max-w-[72px] transition-colors ${
              user.is_premium ? 'text-[#D4AF37]' : 'text-[#9EABCE] group-hover:text-white'
            }`}>
              {user.first_name}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
